/**
 * The clock every freshness verdict reads [mv2-D4].
 *
 * `useFreshness` does not tick. It reads `Date.now()` at render and sets ONE
 * timer for the next moment a source's phase can change on its own (see
 * `nextFreshnessDeadline`), plus 25 ms so the comparison is strictly past it.
 * A panel therefore re-renders when an answer goes stale, not every second.
 *
 * `watchSinceMs` starts at mount and resets when the browser tab becomes
 * visible again: React Query pauses interval polling in a hidden tab, so an
 * answer that aged while nobody was watching reads Refreshing for one maximum
 * age instead of Overdue.
 */
import { useEffect, useState } from 'react';

import { nextFreshnessDeadline, type FreshnessClock, type FreshnessSource } from './liveQueryState.ts';

export function useFreshness(sources: readonly FreshnessSource[]): FreshnessClock {
  const [watchSinceMs, setWatchSinceMs] = useState(() => Date.now());
  const [, setTick] = useState(0);
  const nowMs = Date.now();
  const deadline = nextFreshnessDeadline(sources, watchSinceMs, nowMs);

  useEffect(() => {
    if (deadline === null) return undefined;
    const timer = setTimeout(() => setTick((tick) => tick + 1), Math.max(0, deadline - Date.now()) + 25);
    return () => clearTimeout(timer);
  }, [deadline]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setWatchSinceMs(Date.now());
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return { nowMs, watchSinceMs };
}

/** Wall-clock milliseconds, refreshed every `intervalMs` (for relative labels). */
export function useNow(intervalMs: number): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return nowMs;
}
